import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, HelpCircle } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const Pricing = () => {
  const websitePricing = [
    {
      name: 'ライト',
      price: '30,000円～',
      features: [
        'シンプルな1ページ構成',
        'スマホ対応',
        '基本情報の掲載',
        'お問い合わせフォーム'
      ]
    },
    {
      name: 'スタンダード',
      price: '80,000円～150,000円',
      popular: true,
      features: [
        '複数ページ構成',
        'オリジナルデザイン',
        '写真加工・文章作成',
        'SEO基本対策',
        'お問い合わせ・予約フォーム'
      ]
    },
    {
      name: 'プレミアム',
      price: '150,000円～250,000円',
      features: [
        '充実した複数ページ',
        '高品質デザイン',
        'オリジナル機能追加',
        '写真撮影・プロ編集',
        'Googleアナリティクス設定',
        '公開後3ヶ月サポート'
      ]
    }
  ];

  const snsPricing = [
    {
      name: 'ライト（スタート向け）',
      price: '20,000円/月',
      features: [
        '月４投稿',
        '投稿内容企画',
        '写真編集',
        'キャプション作成',
        'ハッシュタグ選定'
      ]
    },
    {
      name: 'スタンダード（おすすめ・継続）',
      price: '35,000円/月',
      popular: true,
      features: [
        '月８投稿',
        '投稿内容企画',
        '写真編集・加工',
        'キャプション作成',
        'ハッシュタグ最適化',
        '月１レポート'
      ]
    },
    {
      name: 'アクティブ（実務代行）',
      price: '50,000円～70,000円/月',
      features: [
        '月12投稿',
        'ストーリー更新',
        '投稿内容企画',
        '写真撮影・プロ編集',
        'キャンペーン企画',
        '月１詳細レポート',
        '方向性相談'
      ]
    }
  ];

  const faqData = [
    {
      question: 'パソコンが苦手でも大丈夫ですか？',
      answer: 'はい、大丈夫です。専門用語を使わず、やさしく説明いたします。操作も全て代行いたしますので、お任せください。'
    },
    {
      question: '写真や文章の準備は必要ですか？',
      answer: '必要ありません。お店の写真はこちらで撮影・編集いたします。文章も代わりに作成いたしますのでご安心ください。'
    },
    {
      question: '急な変更に対応できますか？',
      answer: 'はい、対応可能です。小さな修正や変更にも迅速に対応いたします。お気軽にご連絡ください。'
    },
    {
      question: 'なぜこの価格で提供できるのですか？',
      answer: '個人事業主で運営コストを抑え、地域密着でサービスを提供しているためです。小規模店舗でも始めやすい価格設定を心がけています。'
    },
    {
      question: '無理な営業はありませんか？',
      answer: '一切ありません。お店の状況やご予算に合わせて、最適なプランをご提案いたします。強引な提案はいたしませんのでご安心ください。'
    },
    {
      question: '途中でプラン変更はできますか？',
      answer: 'はい、可能です。お店の状況に合わせてプランの変更や追加ができます。お気軽にご相談ください。'
    },
    {
      question: 'ホームページの管理はどうなりますか？',
      answer: 'サーバー管理や技術的な部分は当方で行います。お客さまは、内容の修正や更新についてご相談いただく形となります。'
    },
    {
      question: 'ドメイン（URL）はどうなりますか？',
      answer: '既にお持ちのドメインがある場合は、そのままご利用いただけます。新規取得が必要な場合は、原則としてお客さま名義で取得し、設定や管理は当方でサポートいたします。'
    },
    {
      question: 'ロゴがなくても依頼できますか？',
      answer: 'はい、可能です。ロゴをお持ちの場合は、ホームページへ反映いたします。ロゴがない場合でも、テキスト中心のデザインでホームページ制作が可能です。簡易的なロゴ作成（Web利用向け）をご希望の場合は、別途ご相談ください。'
    },
    {
      question: 'SNS用の写真がないのですが…',
      answer: 'ご安心ください。実際にお店に伺い、写真撮影からご対応いたします。スマホでも素敵な写真に編集いたします。'
    },
    {
      question: 'エリア外でも対応可能ですか？',
      answer: '基本的には上郡町・赤穂市・たつの市・相生市を中心に対応していますが、その他の地域もご相談ください。オンラインで対応できるサービスもあります。'
    },
    {
      question: '契約期間はありますか？',
      answer: 'ありません。必要な期間だけご利用いただけます。'
    },
    {
      question: '出張で写真撮影は可能ですか？',
      answer: '地域により対応可能です。まずはご相談ください。'
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="section-spacing bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 fade-in">
            <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#2C2C2C' }}>
              料金プラン
            </h1>
            <p className="text-lg text-gray-700 max-w-2xl mx-auto leading-relaxed">
              小規模事業者さま向けに、<br className="hidden md:inline" />
              「相談だけ」から「完全おまかせ」まで<br className="hidden md:inline" />
              事業の状況に合わせて選べる3つのサポートプランをご用意しています。
            </p>
          </div>

          {/* Main Support Plans */}
          <div className="mb-16">
            {/* Light Support */}
            <div className="bg-white rounded-2xl shadow-lg p-8 mb-8 max-w-4xl mx-auto">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold mb-2" style={{ color: '#2C2C2C' }}>
                    ライトサポート
                  </h2>
                  <p className="text-sm text-gray-500">Light Support</p>
                </div>
                <p className="text-3xl font-bold mt-4 md:mt-0" style={{ color: '#F7C9D3' }}>
                  ¥5,000〜 / 月
                </p>
              </div>
              <p className="text-gray-700 mb-6 leading-relaxed">
                まずは相談から始めたい方向けのプランです。<br />
                Web・SNS・Googleマップに関する相談・アドバイス・見守りを中心としたサポートを行います。
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <h4 className="font-semibold mb-3" style={{ color: '#2C2C2C' }}>サポート内容</h4>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: '#F7C9D3' }} />
                      <span className="text-gray-700 text-sm">Web／SNS／Googleマップに関する相談・アドバイス</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: '#F7C9D3' }} />
                      <span className="text-gray-700 text-sm">現状チェック（表示・情報・設定状況の確認）</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: '#F7C9D3' }} />
                      <span className="text-gray-700 text-sm">改善ポイントのご提案</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: '#F7C9D3' }} />
                      <span className="text-gray-700 text-sm">軽微な内容変更の「やり方」のご案内</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold mb-3" style={{ color: '#2C2C2C' }}>含まれない内容</h4>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <span className="text-gray-400 mr-2">−</span>
                      <span className="text-gray-500 text-sm">投稿・更新などの実作業</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-gray-400 mr-2">−</span>
                      <span className="text-gray-500 text-sm">SNS投稿代行</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-gray-400 mr-2">−</span>
                      <span className="text-gray-500 text-sm">Googleマップ投稿・編集代行</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-gray-400 mr-2">−</span>
                      <span className="text-gray-500 text-sm">Web修正・画像作成</span>
                    </li>
                  </ul>
                </div>
              </div>
              <p className="text-sm text-gray-500 bg-gray-50 p-3 rounded-lg">
                ※ 本プランは相談・助言が中心となり、実作業は含まれません。
              </p>
            </div>

            {/* Standard Support */}
            <div className="bg-white rounded-2xl shadow-lg p-8 mb-8 max-w-4xl mx-auto ring-2 ring-[#F7C9D3] relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="sakura-bg text-white px-4 py-1 rounded-full text-sm font-semibold">
                  人気プラン
                </span>
              </div>
              <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold mb-2" style={{ color: '#2C2C2C' }}>
                    スタンダードサポート
                  </h2>
                  <p className="text-sm text-gray-500">Standard Support</p>
                </div>
                <p className="text-3xl font-bold mt-4 md:mt-0" style={{ color: '#F7C9D3' }}>
                  ¥15,000 前後 / 月
                </p>
              </div>
              <p className="text-gray-700 mb-6 leading-relaxed">
                日々の運用を無理なく任せたい方におすすめのプランです。<br />
                集客につながる改善を一緒に進めていきます。
              </p>
              <div className="mb-6">
                <h4 className="font-semibold mb-3" style={{ color: '#2C2C2C' }}>サポート内容</h4>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: '#F7C9D3' }} />
                    <span className="text-gray-700 text-sm">SNS管理サポート（月4回まで）<br /><span className="text-gray-500">（投稿内容作成サポート または 投稿代行）</span></span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: '#F7C9D3' }} />
                    <span className="text-gray-700 text-sm">Googleマップ投稿・情報改善（月1〜2回）</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: '#F7C9D3' }} />
                    <span className="text-gray-700 text-sm">Web軽微修正（月1回程度）</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: '#F7C9D3' }} />
                    <span className="text-gray-700 text-sm">月1回の簡易レポート・運用相談</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: '#F7C9D3' }} />
                    <span className="text-gray-700 text-sm">今月の改善ポイントご提案</span>
                  </li>
                </ul>
              </div>
              <p className="text-sm text-gray-500 bg-gray-50 p-3 rounded-lg">
                ※ SNS管理サポートは本プランに含まれており、追加費用はかかりません。
              </p>
            </div>

            {/* Active Support */}
            <div className="bg-white rounded-2xl shadow-lg p-8 mb-8 max-w-4xl mx-auto">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold mb-2" style={{ color: '#2C2C2C' }}>
                    アクティブサポート
                  </h2>
                  <p className="text-sm text-gray-500">Active / Hands-on Support</p>
                </div>
                <p className="text-3xl font-bold mt-4 md:mt-0" style={{ color: '#F7C9D3' }}>
                  ¥30,000〜 / 月
                </p>
              </div>
              <p className="text-gray-700 mb-6 leading-relaxed">
                忙しい事業者さま向けの完全おまかせプランです。<br />
                Web・SNS・集客をまとめてサポートします。
              </p>
              <div className="mb-6">
                <h4 className="font-semibold mb-3" style={{ color: '#2C2C2C' }}>サポート内容</h4>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: '#F7C9D3' }} />
                    <span className="text-gray-700 text-sm">SNS運用（週1〜2回投稿）</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: '#F7C9D3' }} />
                    <span className="text-gray-700 text-sm">Web更新・管理・保守</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: '#F7C9D3' }} />
                    <span className="text-gray-700 text-sm">Googleマップ強化・定期運用</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: '#F7C9D3' }} />
                    <span className="text-gray-700 text-sm">キャンペーン企画・集客施策の立案</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: '#F7C9D3' }} />
                    <span className="text-gray-700 text-sm">優先対応・随時相談</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: '#F7C9D3' }} />
                    <span className="text-gray-700 text-sm">サーバー・Web保守管理</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Plan Selection Guide */}
            <div className="bg-gray-50 rounded-2xl p-8 max-w-4xl mx-auto mb-8">
              <h3 className="text-xl font-bold mb-6 text-center" style={{ color: '#2C2C2C' }}>
                プラン選びの目安
              </h3>
              <div className="space-y-4">
                <div className="flex items-center">
                  <span className="text-gray-700">まずは相談したい</span>
                  <span className="mx-4 text-gray-400">→</span>
                  <span className="font-semibold" style={{ color: '#F7C9D3' }}>ライトサポート</span>
                </div>
                <div className="flex items-center">
                  <span className="text-gray-700">日常運用をサポートしてほしい</span>
                  <span className="mx-4 text-gray-400">→</span>
                  <span className="font-semibold" style={{ color: '#F7C9D3' }}>スタンダードサポート</span>
                </div>
                <div className="flex items-center">
                  <span className="text-gray-700">時間がなく全部任せたい</span>
                  <span className="mx-4 text-gray-400">→</span>
                  <span className="font-semibold" style={{ color: '#F7C9D3' }}>アクティブサポート</span>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="max-w-4xl mx-auto mb-8">
              <div className="space-y-2 text-sm text-gray-600">
                <p>※ 表示価格は目安です。業種・内容により調整する場合があります。</p>
                <p>※ 初期設定や制作が必要な場合は、別途お見積りとなります。</p>
                <p>※ 無理な契約や長期縛りはありません。</p>
              </div>
            </div>

            {/* CTA Button */}
            <div className="text-center">
              <Link 
                to="/contact" 
                className="btn-primary inline-block px-8 py-4 rounded-full text-lg font-semibold"
              >
                まずは相談する
              </Link>
            </div>
          </div>

          {/* Support Comparison Table */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-center mb-4" style={{ color: '#2C2C2C' }}>
              サポート内容の比較
            </h2>
            <p className="text-center text-gray-700 mb-8 max-w-2xl mx-auto">
              各プランで対応できる内容をまとめています。<br />
              「どこまで含まれるのか」を事前にご確認ください。
            </p>

            <div className="max-w-4xl mx-auto">
              <h3 className="text-lg font-semibold mb-4 text-center" style={{ color: '#2C2C2C' }}>
                プラン別 サポート範囲一覧
              </h3>

              {/* Mobile-friendly comparison - card style for mobile */}
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                {/* Header Row - Desktop */}
                <div className="hidden md:grid md:grid-cols-4 bg-gray-50 border-b">
                  <div className="p-4 font-semibold text-gray-700">サポート内容</div>
                  <div className="p-4 font-semibold text-center text-gray-700">ライト</div>
                  <div className="p-4 font-semibold text-center text-gray-700">スタンダード</div>
                  <div className="p-4 font-semibold text-center text-white rounded-t-lg" style={{ backgroundColor: '#F7C9D3' }}>アクティブ</div>
                </div>

                {/* Data Rows */}
                {[
                  { name: 'Web相談・アドバイス', light: '対応', standard: '対応', active: '対応' },
                  { name: 'Web軽微修正', light: '対応なし', standard: '月1回程度', active: '対応' },
                  { name: 'Web管理（保守・管理）', light: '対応なし', standard: '対応なし', active: '対応' },
                  { name: 'Webサイト新規制作・リニューアル', light: '対応なし', standard: '対応なし', active: '対応なし（別途お見積り）' },
                  { name: 'サーバー／ホスティング管理', light: '対応なし', standard: '対応なし', active: '対応' },
                  { name: '表示チェック・トラブル対応', light: '対応なし', standard: '対応なし', active: '対応' },
                  { name: 'Googleマップ相談', light: '対応', standard: '対応', active: '対応' },
                  { name: 'Googleマップ投稿・改善', light: '対応なし', standard: '月1〜2回', active: '対応' },
                  { name: 'SNS相談・アドバイス', light: '対応', standard: '対応', active: '対応' },
                  { name: 'SNS管理サポート（投稿含む）', light: '対応なし', standard: '月4回まで', active: '週1〜2回' },
                  { name: 'SNS投稿内容の作成・代行', light: '対応なし', standard: '対応', active: '対応' },
                  { name: 'キャンペーン企画', light: '対応なし', standard: '対応なし', active: '対応' },
                  { name: '優先対応・随時相談', light: '対応なし', standard: '対応なし', active: '対応' },
                ].map((row, index) => (
                  <div key={index}>
                    {/* Desktop View */}
                    <div className="hidden md:grid md:grid-cols-4 border-b last:border-b-0">
                      <div className="p-4 text-sm text-gray-700 bg-white">{row.name}</div>
                      <div className="p-4 text-sm text-center bg-white">
                        <span className={row.light === '対応なし' ? 'text-gray-400' : 'text-gray-700'}>
                          {row.light}
                        </span>
                      </div>
                      <div className="p-4 text-sm text-center bg-white">
                        <span className={row.standard === '対応なし' ? 'text-gray-400' : 'text-gray-700'}>
                          {row.standard}
                        </span>
                      </div>
                      <div className="p-4 text-sm text-center" style={{ backgroundColor: '#FFF7FA' }}>
                        <span className={row.active === '対応なし' ? 'text-gray-400' : 'font-medium text-gray-700'}>
                          {row.active}
                        </span>
                      </div>
                    </div>

                    {/* Mobile View - Card Style */}
                    <div className="md:hidden border-b last:border-b-0 p-4">
                      <div className="font-medium text-gray-700 mb-3">{row.name}</div>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div className="text-center">
                          <div className="text-gray-500 mb-1">ライト</div>
                          <div className={row.light === '対応なし' ? 'text-gray-400' : 'text-gray-700'}>
                            {row.light}
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-gray-500 mb-1">スタンダード</div>
                          <div className={row.standard === '対応なし' ? 'text-gray-400' : 'text-gray-700'}>
                            {row.standard}
                          </div>
                        </div>
                        <div className="text-center rounded p-1" style={{ backgroundColor: '#FFF7FA' }}>
                          <div className="mb-1" style={{ color: '#F7C9D3' }}>アクティブ</div>
                          <div className={row.active === '対応なし' ? 'text-gray-400' : 'font-medium text-gray-700'}>
                            {row.active}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Notes */}
              <div className="mt-4 space-y-1 text-xs text-gray-500">
                <p>※ Webサイトの新規制作・大幅なリニューアルは、すべてのプランに含まれていません。内容に応じて、別途お見積りとなります。</p>
                <p>※「対応なし」は、実作業を含まないことを意味します。</p>
                <p>※ 内容や回数は業種・状況により調整する場合があります。</p>
              </div>
            </div>
          </div>

          {/* FAQ Section for Support Plans */}
          <div className="mb-16">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-8">
                <HelpCircle className="w-12 h-12 mx-auto mb-4" style={{ color: '#F7C9D3' }} />
                <h2 className="text-3xl font-bold" style={{ color: '#2C2C2C' }}>
                  よくあるご質問（FAQ）
                </h2>
              </div>
              <Accordion type="single" collapsible className="w-full bg-white rounded-2xl shadow-lg p-4">
                <AccordionItem value="faq-1">
                  <AccordionTrigger className="text-left text-sm md:text-base">
                    ライトサポートでは、実際の作業もしてもらえますか？
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 text-sm">
                    ライトサポートは相談・アドバイスを中心としたプランです。実作業は含まれていません。
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="faq-2">
                  <AccordionTrigger className="text-left text-sm md:text-base">
                    スタンダードサポートにはSNS管理サポートは含まれていますか？
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 text-sm">
                    含まれています。月4回までのSNS投稿サポートが追加料金なしで含まれます。
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="faq-3">
                  <AccordionTrigger className="text-left text-sm md:text-base">
                    スタンダードサポートでは、SNSの投稿は誰が行いますか？
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 text-sm">
                    内容作成サポート、または投稿代行のどちらかを対応します。
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="faq-4">
                  <AccordionTrigger className="text-left text-sm md:text-base">
                    スタンダードサポートでWebの管理や保守もしてもらえますか？
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 text-sm">
                    軽微な修正のみ対応し、Web管理・保守は含まれていません。
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="faq-5">
                  <AccordionTrigger className="text-left text-sm md:text-base">
                    ホスティングやサーバー管理はどのプランに含まれますか？
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 text-sm">
                    アクティブサポートのみ対応しています。
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="faq-6">
                  <AccordionTrigger className="text-left text-sm md:text-base">
                    Googleマップの投稿や改善はどのプランで対応してもらえますか？
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 text-sm">
                    スタンダードサポート以上で対応します。
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="faq-7">
                  <AccordionTrigger className="text-left text-sm md:text-base">
                    キャンペーン企画や集客施策はお願いできますか？
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 text-sm">
                    アクティブサポートで対応しています。
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="faq-8">
                  <AccordionTrigger className="text-left text-sm md:text-base">
                    どのプランを選べばよいか分かりません。
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 text-sm">
                    <p className="mb-2">目的に応じた目安をご参考ください。</p>
                    <ul className="space-y-1 text-sm">
                      <li>・まずは相談したい → ライトサポート</li>
                      <li>・日常運用をサポートしてほしい → スタンダードサポート</li>
                      <li>・時間がなく全部任せたい → アクティブサポート</li>
                    </ul>
                    <p className="mt-3">迷われる場合は、お気軽にご相談ください。</p>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>

          {/* Website Pricing */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-center mb-8" style={{ color: '#2C2C2C' }}>
              Webサイト制作
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {websitePricing.map((plan, index) => (
                <div 
                  key={index} 
                  className={`bg-white rounded-2xl shadow-lg p-8 hover-lift ${
                    plan.popular ? 'ring-2 ring-[#F7C9D3] relative' : ''
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="sakura-bg text-white px-4 py-1 rounded-full text-sm font-semibold">
                        人気
                      </span>
                    </div>
                  )}
                  <h3 className="text-2xl font-bold mb-4" style={{ color: '#2C2C2C' }}>
                    {plan.name}
                  </h3>
                  <p className="text-3xl font-bold mb-6" style={{ color: '#F7C9D3' }}>
                    {plan.price}
                  </p>
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: '#F7C9D3' }} />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link 
                    to="/contact" 
                    className="btn-primary block text-center py-3 rounded-full font-medium"
                  >
                    お問い合わせ
                  </Link>
                </div>
              ))}
            </div>
            <p className="text-center text-sm text-gray-600 mt-6">
              ※ 内容・ご予算に応じて柔軟にご提案します
            </p>
            <p className="text-center text-sm text-gray-600 mt-2">
              ※ ホームページは管理・運用込みのサービス提供となります
            </p>
          </div>

          {/* SNS Pricing */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-center mb-8" style={{ color: '#2C2C2C' }}>
              SNS運用サポート
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {snsPricing.map((plan, index) => (
                <div 
                  key={index} 
                  className={`bg-white rounded-2xl shadow-lg p-8 hover-lift ${
                    plan.popular ? 'ring-2 ring-[#F7C9D3] relative' : ''
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="sakura-bg text-white px-4 py-1 rounded-full text-sm font-semibold">
                        人気
                      </span>
                    </div>
                  )}
                  <h3 className="text-2xl font-bold mb-4" style={{ color: '#2C2C2C' }}>
                    {plan.name}
                  </h3>
                  <p className="text-3xl font-bold mb-6" style={{ color: '#F7C9D3' }}>
                    {plan.price}
                  </p>
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: '#F7C9D3' }} />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link 
                    to="/contact" 
                    className="btn-primary block text-center py-3 rounded-full font-medium"
                  >
                    お問い合わせ
                  </Link>
                </div>
              ))}
            </div>
            <p className="text-center text-sm text-gray-600 mt-6">
              ※ 内容・ご予算に応じて柔軟にご提案します
            </p>
          </div>

          {/* Hosting & Management Plans */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-center mb-8" style={{ color: '#2C2C2C' }}>
              ホスティング & Web管理プラン
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              <div className="bg-white rounded-2xl shadow-lg p-8 hover-lift">
                <h3 className="text-2xl font-bold mb-1" style={{ color: '#2C2C2C' }}>
                  ライト
                </h3>
                <p className="text-sm text-gray-500 mb-4">（基本管理）</p>
                <p className="text-3xl font-bold mb-6" style={{ color: '#F7C9D3' }}>
                  5,000円/月
                </p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: '#F7C9D3' }} />
                    <span className="text-gray-700">サーバー管理</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: '#F7C9D3' }} />
                    <span className="text-gray-700">ドメイン管理</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: '#F7C9D3' }} />
                    <span className="text-gray-700">セキュリティ対策</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: '#F7C9D3' }} />
                    <span className="text-gray-700">月1回の軽微な更新</span>
                  </li>
                </ul>
                <Link 
                  to="/contact" 
                  className="btn-primary block text-center py-3 rounded-full font-medium"
                >
                  お問い合わせ
                </Link>
              </div>

              <div className="bg-white rounded-2xl shadow-lg p-8 hover-lift ring-2 ring-[#F7C9D3] relative">
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="sakura-bg text-white px-4 py-1 rounded-full text-sm font-semibold">
                    人気
                  </span>
                </div>
                <h3 className="text-2xl font-bold mb-1" style={{ color: '#2C2C2C' }}>
                  スタンダード
                </h3>
                <p className="text-sm text-gray-500 mb-4">（継続管理）</p>
                <p className="text-3xl font-bold mb-6" style={{ color: '#F7C9D3' }}>
                  10,000円/月
                </p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: '#F7C9D3' }} />
                    <span className="text-gray-700">ライトプランの内容全て</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: '#F7C9D3' }} />
                    <span className="text-gray-700">月3回までの更新対応</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: '#F7C9D3' }} />
                    <span className="text-gray-700">バックアップ</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: '#F7C9D3' }} />
                    <span className="text-gray-700">アクセス解析レポート</span>
                  </li>
                </ul>
                <Link 
                  to="/contact" 
                  className="btn-primary block text-center py-3 rounded-full font-medium"
                >
                  お問い合わせ
                </Link>
              </div>

              <div className="bg-white rounded-2xl shadow-lg p-8 hover-lift">
                <h3 className="text-2xl font-bold mb-1" style={{ color: '#2C2C2C' }}>
                  アクティブ
                </h3>
                <p className="text-sm text-gray-500 mb-4">（優先対応）</p>
                <p className="text-3xl font-bold mb-6" style={{ color: '#F7C9D3' }}>
                  15,000円/月
                </p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: '#F7C9D3' }} />
                    <span className="text-gray-700">スタンダードの内容全て</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: '#F7C9D3' }} />
                    <span className="text-gray-700">無制限の更新対応</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: '#F7C9D3' }} />
                    <span className="text-gray-700">優先サポート</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: '#F7C9D3' }} />
                    <span className="text-gray-700">月次改善提案</span>
                  </li>
                </ul>
                <Link 
                  to="/contact" 
                  className="btn-primary block text-center py-3 rounded-full font-medium"
                >
                  お問い合わせ
                </Link>
              </div>
            </div>
            <p className="text-center text-sm text-gray-600 mt-6">
              ※ 内容・ご予算に応じて柔軟にご提案します
            </p>
            <p className="text-center text-sm text-gray-600 mt-2">
              ※ サポート内容はサイト規模・更新内容により、回数や対応方法をご相談のうえ決定いたします。
            </p>
          </div>

          {/* Other Services */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-center mb-4" style={{ color: '#2C2C2C' }}>
              その他のサービス
            </h2>
            <p className="text-center text-sm text-gray-600 mb-8">
              ※ 単発でのご相談・ご依頼も可能ですが、<br className="hidden md:inline" />
              継続サポートをご利用いただくことで、より効果的な改善が可能です。
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <h3 className="text-2xl font-bold mb-4" style={{ color: '#2C2C2C' }}>
                  マーケティングサポート
                </h3>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: '#F7C9D3' }} />
                    <span className="text-gray-700">Googleマップ最適化：15,000円～</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: '#F7C9D3' }} />
                    <span className="text-gray-700">キャンペーン企画：10,000円～</span>
                  </li>
                </ul>
                <Link to="/services/meo" className="text-[#F7C9D3] hover:text-[#2C2C2C] font-medium">
                  詳細を見る →
                </Link>
              </div>

              <div className="bg-white rounded-2xl shadow-lg p-8">
                <h3 className="text-2xl font-bold mb-4" style={{ color: '#2C2C2C' }}>
                  ビジネスコンサルティング
                </h3>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: '#F7C9D3' }} />
                    <span className="text-gray-700">単発：10,000円～</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: '#F7C9D3' }} />
                    <span className="text-gray-700">月額：25,000円～40,000円</span>
                  </li>
                </ul>
                <Link to="/services/consulting" className="text-[#F7C9D3] hover:text-[#2C2C2C] font-medium">
                  詳細を見る →
                </Link>
              </div>
            </div>
            <p className="text-center text-sm text-gray-600 mt-6">
              ※ 内容・ご予算に応じて柔軟にご提案します
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-spacing bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <HelpCircle className="w-12 h-12 mx-auto mb-4" style={{ color: '#F7C9D3' }} />
              <h2 className="text-3xl font-bold" style={{ color: '#2C2C2C' }}>
                よくあるご質問
              </h2>
            </div>
            <Accordion type="single" collapsible className="w-full">
              {faqData.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="text-left">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Web制作・ホスティング（全国対応）Section */}
      <section className="section-spacing bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#2C2C2C' }}>
                Webサイト制作・ホスティング（全国対応）
              </h2>
              <p className="text-gray-700 leading-relaxed">
                SNS経由でのご相談に限り、<br className="hidden md:inline" />
                Webサイト制作・ホスティングのみのサービスを<br className="hidden md:inline" />
                対応エリア外の事業者さまにも提供しています。
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                「まずは最低限のホームページを持ちたい」<br />
                「管理までまとめて任せたい」<br />
                そんな方向けの、シンプルで安心なWebサービスです。
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
              {/* サービス内容 */}
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <h3 className="text-xl font-bold mb-6" style={{ color: '#2C2C2C' }}>
                  サービス内容
                </h3>
                
                <div className="mb-6">
                  <h4 className="font-semibold mb-3 text-gray-700">Webサイト制作（1〜3ページ程度）</h4>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: '#F7C9D3' }} />
                      <span className="text-gray-600 text-sm">スマートフォン対応</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: '#F7C9D3' }} />
                      <span className="text-gray-600 text-sm">シンプルで見やすいデザイン</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: '#F7C9D3' }} />
                      <span className="text-gray-600 text-sm">お問い合わせフォーム設置</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: '#F7C9D3' }} />
                      <span className="text-gray-600 text-sm">基本的なSEO設定</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold mb-3 text-gray-700">ホスティング・Web管理（月額）</h4>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: '#F7C9D3' }} />
                      <span className="text-gray-600 text-sm">サーバー管理</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: '#F7C9D3' }} />
                      <span className="text-gray-600 text-sm">表示チェック</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: '#F7C9D3' }} />
                      <span className="text-gray-600 text-sm">軽微なテキスト修正（月1回）</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: '#F7C9D3' }} />
                      <span className="text-gray-600 text-sm">セキュリティ・保守対応</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* 料金 */}
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <h3 className="text-xl font-bold mb-6" style={{ color: '#2C2C2C' }}>
                  料金
                </h3>
                
                <div className="space-y-6">
                  <div className="border-b pb-4">
                    <p className="text-gray-600 text-sm mb-2">Webサイト制作費（1回）</p>
                    <p className="text-2xl font-bold" style={{ color: '#F7C9D3' }}>¥30,000〜</p>
                  </div>
                  <div className="pb-4">
                    <p className="text-gray-600 text-sm mb-2">ホスティング・Web管理費</p>
                    <p className="text-2xl font-bold" style={{ color: '#F7C9D3' }}>¥5,000 / 月</p>
                  </div>
                </div>

                <p className="text-xs text-gray-500 mt-6 bg-gray-50 p-3 rounded-lg">
                  ※ 内容・ページ数により、別途お見積りとなる場合があります。
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
              {/* 含まれない内容 */}
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <h3 className="text-xl font-bold mb-6" style={{ color: '#2C2C2C' }}>
                  含まれない内容
                </h3>
                <p className="text-gray-600 text-sm mb-4">本サービスには、以下は含まれていません。</p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <span className="text-gray-400 mr-2">−</span>
                    <span className="text-gray-500 text-sm">SNS運用・投稿代行</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gray-400 mr-2">−</span>
                    <span className="text-gray-500 text-sm">Googleマップ最適化</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gray-400 mr-2">−</span>
                    <span className="text-gray-500 text-sm">キャンペーン企画・集客支援</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gray-400 mr-2">−</span>
                    <span className="text-gray-500 text-sm">継続的なマーケティングサポート</span>
                  </li>
                </ul>
                <p className="text-xs text-gray-500 mt-4">
                  これらをご希望の場合は、対応エリア内向けのSLBSサポートプランをご案内いたします。
                </p>
              </div>

              {/* 対応について & こんな方におすすめ */}
              <div className="space-y-8">
                <div className="bg-white rounded-2xl shadow-lg p-8">
                  <h3 className="text-xl font-bold mb-4" style={{ color: '#2C2C2C' }}>
                    対応について
                  </h3>
                  <ul className="space-y-2 text-sm text-gray-600 mb-4">
                    <li>・対応方法：メール対応</li>
                    <li>・対象エリア：全国（SNS・Webからのお問い合わせ）</li>
                    <li>・本サービスはWeb制作・管理に限定されます</li>
                  </ul>
                  <p className="text-sm text-gray-600 leading-relaxed bg-gray-50 p-4 rounded-lg">
                    本サービスでは、メールでのやり取りを基本とすることで、<br className="hidden md:inline" />
                    料金を抑えつつ、作業内容を明確にし、正確で安定した対応を行っています。<br className="hidden md:inline" />
                    低価格でも無理のない運営を行うため、リアルタイムでの打ち合わせは含まれていません。
                  </p>
                </div>

                <div className="bg-white rounded-2xl shadow-lg p-8">
                  <h3 className="text-xl font-bold mb-4" style={{ color: '#2C2C2C' }}>
                    こんな方におすすめです
                  </h3>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: '#F7C9D3' }} />
                      <span className="text-gray-600 text-sm">最低限のホームページを持ちたい方</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: '#F7C9D3' }} />
                      <span className="text-gray-600 text-sm">サーバー管理や設定が不安な方</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: '#F7C9D3' }} />
                      <span className="text-gray-600 text-sm">まずはシンプルに始めたい個人事業主さま</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: '#F7C9D3' }} />
                      <span className="text-gray-600 text-sm">遠方のため、Web制作のみを希望される方</span>
                    </li>
                  </ul>
                </div>

                {/* FAQ for this service */}
                <div className="bg-white rounded-2xl shadow-lg p-8">
                  <h3 className="text-xl font-bold mb-4" style={{ color: '#2C2C2C' }}>
                    よくあるご質問
                  </h3>
                  <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value="web-faq-1">
                      <AccordionTrigger className="text-left text-sm">
                        打ち合わせは可能ですか？
                      </AccordionTrigger>
                      <AccordionContent className="text-gray-600 text-sm">
                        <p className="mb-3">
                          本サービス（Webサイト制作・ホスティング／全国対応）は、メールでの対応を基本としています。<br />
                          内容を整理し、正確に反映するため、やり取りは原則としてメールで行っています。
                        </p>
                        <p className="text-xs text-gray-500 bg-gray-50 p-3 rounded">
                          ※ 対面でのご相談をご希望の場合は、対応エリア内向けのSLBSサポートプランをご案内しています。
                        </p>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>
              </div>
            </div>

            {/* 注意書き */}
            <div className="text-center">
              <div className="inline-block text-left text-xs text-gray-500 space-y-1">
                <p>※ 本サービスはWebサイト制作・管理に特化した内容です。</p>
                <p>※ 対面サポートや地域密着型の集客支援は含まれていません。</p>
                <p>※ 長期契約の縛りはありません。</p>
              </div>
            </div>

            {/* CTA */}
            <div className="text-center mt-8">
              <Link 
                to="/contact" 
                className="btn-primary inline-block px-8 py-4 rounded-full text-lg font-semibold"
              >
                お問い合わせ・ご相談
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-spacing" style={{ backgroundColor: '#F7C9D3' }}>
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#2C2C2C' }}>
            お気軽にご相談ください
          </h2>
          <p className="text-lg mb-8" style={{ color: '#2C2C2C' }}>
            無料でお話をお伺いいたします
          </p>
          <Link to="/contact" className="btn-primary inline-block px-8 py-4 rounded-full text-lg font-semibold">
            お問い合わせフォームへ
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Pricing;